<template>
    <!-- Gebäude → Wohnung, je Ebene mit eigenem Entscheid. Bei wenigen Einträgen oder nur einem
         Gebäude ohne Gebäude-Ebene direkt die Wohnungen. -->
    <ul :class="aligned ? 'space-y-1.5' : 'space-y-1'">
        <template v-if="showBuildings">
            <li v-for="building in buildings" :key="building.name" class="text-xs">
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="flex items-center font-semibold text-gray-900 mr-auto focus:outline-none focus:ring-2 focus:ring-minplan-blue rounded"
                        :aria-expanded="!!open[building.name]"
                        @click="open = { ...open, [building.name]: !open[building.name] }"
                    >
                        <ChevronDownIcon
                            v-if="open[building.name]"
                            class="h-4 w-4 mr-1 text-gray-500"
                            aria-hidden="true"
                        />
                        <ChevronRightIcon
                            v-else
                            class="h-4 w-4 mr-1 text-gray-500"
                            aria-hidden="true"
                        />
                        {{ building.name || $t("templateTransfer.noBuilding") }} ·
                        {{ building.entries.length }}
                    </button>
                    <decision-toggle
                        :model-value="decisionOf(building.entries)"
                        :choices="choicesOf(building.entries)"
                        :label="building.name"
                        @update:model-value="$emit('set', building.entries, $event)"
                    />
                </div>
                <ul v-if="open[building.name]" class="mt-1 ml-5 space-y-1">
                    <apartment-row
                        v-for="entry in building.entries"
                        :key="entry.item.key"
                        :entry="entry"
                    />
                </ul>
            </li>
        </template>
        <template v-else>
            <apartment-row
                v-for="entry in entries"
                :key="entry.item.key"
                :entry="entry"
            />
        </template>
    </ul>
</template>

<script>
import { defineComponent, h } from "vue";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/vue/outline";
import DecisionToggle from "@/Pages/Projects/Components/DecisionToggle";

// Plan vorlage-auf-wohnungen (Task 10): gemeinsamer Baum für Normalfälle und Ausnahmen in der Vorschau.
// Die Entscheide gehören dem Dialog; der Baum meldet Änderungen über `set(entries, decision)`.
export default defineComponent({
    props: {
        entries: { type: Array, required: true },
        decisions: { type: Object, required: true },
        choicesFor: { type: Function, required: true },
        // Zusatztext je Wohnung (z. B. eigener Wert, Grund)
        detailFor: { type: Function, default: null },
        // Plan 2026-10-29 (Staging-Runde 2): Wohnung, Angabe und Knöpfe in festen Spalten,
        // Namensspalte 11.25rem; zu lange Namen und Angaben enden mit «…», der volle Text
        // steht im Tooltip. Ohne das Prop bleibt die Zeile, wie sie war.
        aligned: { type: Boolean, default: false },
    },
    emits: ["set"],
    components: {
        DecisionToggle,
        ChevronDownIcon,
        ChevronRightIcon,
        ApartmentRow: {
            props: ["entry"],
            inject: ["tree"],
            render() {
                const tree = this.tree;
                const entry = this.entry;
                if (tree.aligned) return this.alignedRow(tree, entry);
                return h("li", { class: "flex items-center gap-2 text-xs" }, [
                    h(
                        "span",
                        { class: "font-semibold text-gray-900 whitespace-nowrap" },
                        entry.plan.name
                    ),
                    h(
                        "span",
                        { class: "text-gray-500 mr-auto" },
                        tree.detailFor ? tree.detailFor(entry) : ""
                    ),
                    h(DecisionToggle, {
                        modelValue: tree.decisions[entry.item.key],
                        choices: tree.choicesFor(entry.item),
                        label: entry.plan.name,
                        "onUpdate:modelValue": (value) =>
                            tree.$emit("set", [entry], value),
                    }),
                ]);
            },
            methods: {
                // `truncate` bringt `white-space: nowrap` mit, eine zweite Klasse braucht es nicht
                alignedRow(tree, entry) {
                    const detail = tree.detailFor ? tree.detailFor(entry) : "";
                    return h(
                        "li",
                        {
                            class: "grid grid-cols-[11.25rem_minmax(0,1fr)_auto] items-center gap-x-3 text-xs",
                        },
                        [
                            h(
                                "span",
                                {
                                    class: "font-semibold text-gray-900 truncate",
                                    title: entry.plan.name,
                                },
                                entry.plan.name
                            ),
                            h(
                                "span",
                                {
                                    class: "text-gray-500 truncate",
                                    title: detail || undefined,
                                },
                                detail
                            ),
                            h(DecisionToggle, {
                                modelValue: tree.decisions[entry.item.key],
                                choices: tree.choicesFor(entry.item),
                                label: entry.plan.name,
                                "onUpdate:modelValue": (value) =>
                                    tree.$emit("set", [entry], value),
                            }),
                        ]
                    );
                },
            },
        },
    },
    provide() {
        return { tree: this };
    },
    data() {
        return { open: {} };
    },
    computed: {
        buildings() {
            const map = new Map();
            this.entries.forEach((entry) => {
                const name = entry.plan.building || "";
                if (!map.has(name)) map.set(name, { name, entries: [] });
                map.get(name).entries.push(entry);
            });
            return [...map.values()];
        },
        showBuildings() {
            return this.buildings.length > 1 && this.entries.length > 5;
        },
    },
    methods: {
        decisionOf(entries) {
            const values = [
                ...new Set(entries.map((entry) => this.decisions[entry.item.key])),
            ];
            return values.length === 1 ? values[0] : null;
        },
        choicesOf(entries) {
            return entries.every((entry) => this.choicesFor(entry.item).includes("apply"))
                ? ["apply", "keep", "discard"]
                : ["keep", "discard"];
        },
    },
});
</script>
